//
// ============================================================================
// IMPORTS
// ============================================================================
//

import { VisitsClient } from "@/services/api/domains/visits/visits.client";
import type {
  Visit,
  PaginationParams,
  PaginatedResponse,
  VisitResults,
  VisitBalance,
  BalanceSale,
  BalanceProduct,
  BalancePayment,
} from "@/services/api/domains/visits/visits.types";
import type { ErrorType } from "@/composables/utils/useErrorBoundary";
import { isChaosEmptySignal } from "@/composables/utils/useChaos";

// Re-export for consumers that import from the composable
export type { BalanceSale, BalanceProduct, BalancePayment };

/**
 * Structured error state returned by useVisits.
 * Contains the classified error type and a localized user-friendly message.
 */
export interface VisitsError {
  type: ErrorType;
  message: string;
}

//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * Visits Management Composable
 *
 * Manages patient visit data fetching and state for the LIS Portal.
 *
 * Responsibilities:
 * - Fetch visits with pagination and filtering
 * - Fetch visit results
 * - Fetch visit financial balance
 * - Cache data in state
 * - Handle errors with user-friendly messages
 *
 * @param key - Unique key for state isolation (default: "visits")
 */
export const useVisits = (key = "visits") => {
  //
  // ============================================================================
  // COMPOSABLES
  // ============================================================================
  //

  const config = useRuntimeConfig();
  const { t } = useI18n();
  const { getToken } = useAuth();
  const { classifyError, getErrorMessage } = useErrorBoundary();
  const { showSnackbar } = useSnackbar();

  //
  // ============================================================================
  // STATE
  // ============================================================================
  //

  const loading = useState<boolean>(`${key}.loading`, () => false);

  /** Visits state — restored from sessionStorage so stale data survives a page reload */
  const visits = useState<PaginatedResponse<Visit> | null>(`${key}.visits`, () => {
    if (import.meta.client) {
      const stored = sessionStorage.getItem(`${key}.visits`);
      if (stored) {
        try {
          return JSON.parse(stored) as PaginatedResponse<Visit>;
        } catch {
          return null;
        }
      }
    }
    return null;
  });

  watch(visits, (val) => {
    if (import.meta.client) {
      if (val) {
        sessionStorage.setItem(`${key}.visits`, JSON.stringify(val));
      } else {
        sessionStorage.removeItem(`${key}.visits`);
      }
    }
  });

  /** Visit results — restored from sessionStorage so stale data survives a page reload */
  const currentVisitResults = useState<VisitResults | null>(`${key}.currentResults`, () => {
    if (import.meta.client) {
      const stored = sessionStorage.getItem(`${key}.currentResults`);
      if (stored) {
        try {
          return JSON.parse(stored) as VisitResults;
        } catch {
          return null;
        }
      }
    }
    return null;
  });

  watch(currentVisitResults, (val) => {
    if (import.meta.client) {
      if (val) {
        sessionStorage.setItem(`${key}.currentResults`, JSON.stringify(val));
      } else {
        sessionStorage.removeItem(`${key}.currentResults`);
      }
    }
  });

  /** Visit balance — restored from sessionStorage so stale data survives a page reload */
  const currentVisitBalance = useState<VisitBalance | null>(`${key}.currentBalance`, () => {
    if (import.meta.client) {
      const stored = sessionStorage.getItem(`${key}.currentBalance`);
      if (stored) {
        try {
          return JSON.parse(stored) as VisitBalance;
        } catch {
          return null;
        }
      }
    }
    return null;
  });

  watch(currentVisitBalance, (val) => {
    if (import.meta.client) {
      if (val) {
        sessionStorage.setItem(`${key}.currentBalance`, JSON.stringify(val));
      } else {
        sessionStorage.removeItem(`${key}.currentBalance`);
      }
    }
  });
  const error = useState<VisitsError | null>(`${key}.error`, () => null);

  /** Maximum age (ms) before a cached page entry is considered stale and silently revalidated */
  const STALE_TIME_MS = 60_000;

  /** Per-page cache keyed by "pageNumber-pageSize-filter" */
  interface PageCacheEntry {
    data: PaginatedResponse<Visit>;
    fetchedAt: number;
  }

  /**
   * In-memory page cache — survives client-side navigation via useState.
   * On browser reload the Map starts empty; call seedPageCache() from the
   * page composable to pre-populate it with the sessionStorage-restored
   * visits data so the initial fetch is a cache HIT (stale → silent
   * revalidation) instead of a miss that would show a loading skeleton.
   */
  const pageCache = useState<Map<string, PageCacheEntry>>(`${key}.pageCache`, () => new Map());

  /** Per-visit-ID results cache to avoid redundant network calls (e.g. flags) */
  interface ResultsCacheEntry {
    data: VisitResults;
    fetchedAt: number;
  }
  const resultsCache = useState<Map<number, ResultsCacheEntry>>(
    `${key}.resultsCache`,
    () => new Map(),
  );

  /**
   * Set to true when GET /visits/{id}/results returns 404.
   * For OTP patients the visit ID is derived from their Keycloak username and
   * is guaranteed to exist, so a 404 means the patient has not yet paid.
   * Reset to false by clearResultsCache() before each manual refresh.
   * Persisted to sessionStorage so the flag survives a page refresh.
   */
  const isPaymentRequired = useState<boolean>(`${key}.paymentRequired`, () => {
    const stored = sessionStorage.getItem(`${key}.paymentRequired`);
    return stored === "true";
  });

  watch(isPaymentRequired, (val) => {
    sessionStorage.setItem(`${key}.paymentRequired`, String(val));
  });

  //
  // ============================================================================
  // METHODS
  // ============================================================================
  //

  /**
   * Monotonically increasing version counter for stale-fetch protection.
   * Each call to fetchFromNetwork increments this; only the latest version
   * is allowed to write to shared state (visits, loading, error). This
   * prevents a cancelled request's finally block from clearing loading
   * for a newer request.
   */
  let fetchVersion = 0;

  /**
   * Track in-flight prefetch promises by cache key so that fetchVisits can
   * piggyback on an already-running prefetch instead of making a duplicate
   * network request for the same page.
   */
  const prefetchPromises = new Map<string, Promise<void>>();

  /**
   * Build a deterministic cache key from pagination params.
   */
  function pageCacheKey(params?: PaginationParams): string {
    const p = params?.pageNumber ?? 1;
    const s = params?.pageSize ?? 10;
    const f = params?.filter ?? "";
    return `${p}-${s}-${f}`;
  }

  /**
   * Fetch visits (paginated) with stale-while-revalidate page cache.
   *
   * 1. Cache hit + fresh (< STALE_TIME_MS) → serve immediately, no fetch.
   * 2. Cache hit + stale → serve immediately, silent revalidate in background.
   * 3. Cache miss → skeleton → fetch → write to map.
   *
   * When `forceRefresh` is true, skip the cache entirely (used by manual refresh).
   *
   * @param params       - Pagination parameters
   * @param signal       - Optional abort signal
   * @param silent       - When true, skip loading state if cache already has data
   * @param forceRefresh - When true, bypass the cache entirely
   * @param suppressSnackbar - When true, skip the built-in snackbar on refresh failure
   *                           (the caller handles error notification itself)
   */
  const fetchVisits = async (
    params?: PaginationParams,
    signal?: AbortSignal | null,
    silent = false,
    forceRefresh = false,
    suppressSnackbar = false,
  ): Promise<PaginatedResponse<Visit> | null> => {
    const cacheKey = pageCacheKey(params);
    const cached = pageCache.value.get(cacheKey);
    const now = Date.now();

    // Cache hit — serve immediately
    if (!forceRefresh && cached) {
      visits.value = cached.data;

      // Serving from cache — ensure loading is cleared (defensive guard
      // against leaks from aborted piggyback or fetchFromNetwork calls)
      loading.value = false;

      // Still fresh — no network needed
      if (now - cached.fetchedAt < STALE_TIME_MS) return cached.data;

      // Stale — silent background revalidate
      void fetchFromNetwork(params, signal, true, cacheKey, suppressSnackbar);
      return cached.data;
    }

    // Cache miss — check for an in-flight prefetch for the same page.
    // If one exists, piggyback on it instead of making a duplicate request.
    if (!forceRefresh) {
      const inflight = prefetchPromises.get(cacheKey);
      if (inflight) {
        const myVersion = ++fetchVersion;
        loading.value = true;
        error.value = null;
        await inflight;

        // Stale or aborted: clear loading and bail out
        if (myVersion !== fetchVersion || signal?.aborted) {
          if (myVersion === fetchVersion) loading.value = false;
          return null;
        }

        const prefetched = pageCache.value.get(cacheKey);
        if (prefetched) {
          visits.value = prefetched.data;
          loading.value = false;
          return prefetched.data;
        }
        // Prefetch failed — fall through to fetchFromNetwork
        loading.value = false;
      }
    }

    // True cache miss or forced refresh — fetch from network with loading skeleton.
    return fetchFromNetwork(params, signal, false, cacheKey, suppressSnackbar);
  };

  /**
   * Internal: actually hit the network, write to cache + visits state.
   *
   * Error handling:
   * - Always sets `error.value` on failure (both cached and non-cached paths)
   *   so page composables can react to the error and decide on UI.
   * - When cached data exists AND `suppressSnackbar` is false, also fires a
   *   built-in snackbar. Page composables that want custom snackbar behavior
   *   (e.g. a Retry action) should pass `suppressSnackbar=true` and watch
   *   `error` to handle notification themselves.
   */
  async function fetchFromNetwork(
    params: PaginationParams | undefined,
    signal: AbortSignal | null | undefined,
    silent: boolean,
    cacheKey: string,
    suppressSnackbar = false,
  ): Promise<PaginatedResponse<Visit> | null> {
    const myVersion = ++fetchVersion;
    const hasCachedData = visits.value !== null;
    const showLoading = !silent || !hasCachedData;

    if (showLoading) {
      loading.value = true;
      error.value = null;
    }

    try {
      const token = await getToken();
      const client = new VisitsClient(config.public.API_BASE_URL as string, token);
      const data = await client.getVisits(params, signal);

      // Stale response guard — a newer fetch has started; discard this result
      if (myVersion !== fetchVersion) return data;

      visits.value = data;

      pageCache.value.set(cacheKey, { data, fetchedAt: Date.now() });

      error.value = null;
      return data;
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return null;

      // Chaos force-empty: return a typed empty page so the UI shows
      // the same "no data" state as a real zero-result API response.
      if (isChaosEmptySignal(err) && myVersion === fetchVersion) {
        const emptyResponse: PaginatedResponse<Visit> = {
          items: [],
          totalCount: 0,
          pageNumber: params?.pageNumber ?? 1,
          pageSize: params?.pageSize ?? 10,
          totalPages: 0,
          hasNext: false,
          hasPrevious: false,
        };
        visits.value = emptyResponse;
        pageCache.value.set(cacheKey, { data: emptyResponse, fetchedAt: Date.now() });
        error.value = null;
        return emptyResponse;
      }

      // 404 from the visits list endpoint means "no matching results" —
      // treat as an empty page, not as an error. The table will show
      // "filtered-empty" (when a filter is active) or "empty" naturally.
      const statusCode = (err as { statusCode?: number }).statusCode;
      if (statusCode === 404 && myVersion === fetchVersion) {
        const emptyResponse: PaginatedResponse<Visit> = {
          items: [],
          totalCount: 0,
          pageNumber: params?.pageNumber ?? 1,
          pageSize: params?.pageSize ?? 10,
          totalPages: 0,
          hasNext: false,
          hasPrevious: false,
        };
        visits.value = emptyResponse;
        pageCache.value.set(cacheKey, { data: emptyResponse, fetchedAt: Date.now() });
        error.value = null;
        return emptyResponse;
      }

      if (myVersion === fetchVersion) {
        const type = classifyError(err);
        if (type !== "abort") {
          // Always set error state so page composables can react
          error.value = {
            type,
            message: getErrorMessage(type, undefined, (err as Error)?.message),
          };

          // Built-in snackbar for refresh failures (cached data stays visible).
          // Suppressed when the page composable handles its own notification.
          if (hasCachedData && !suppressSnackbar) {
            showSnackbar(getErrorMessage(type, undefined, (err as Error)?.message), {
              color: "error",
              icon: "mdi-alert-circle-outline",
            });
          }
        }
      }
      console.error("[useVisits.fetchFromNetwork]", err);
      return null;
    } finally {
      if (myVersion === fetchVersion && showLoading) {
        loading.value = false;
      }
    }
  }

  /**
   * Prefetch a page into cache without updating the visible visits state.
   * Fires in the background; errors are silently swallowed.
   *
   * @param params - Pagination parameters for the page to prefetch
   * @param signal - Optional abort signal
   */
  const prefetchVisits = async (
    params: PaginationParams,
    signal?: AbortSignal | null,
  ): Promise<void> => {
    const cacheKey = pageCacheKey(params);
    const cached = pageCache.value.get(cacheKey);
    if (cached && Date.now() - cached.fetchedAt < STALE_TIME_MS) return;

    // Already prefetching this key — don't duplicate
    if (prefetchPromises.has(cacheKey)) return;

    const promise = (async () => {
      try {
        const token = await getToken();
        const client = new VisitsClient(config.public.API_BASE_URL as string, token);
        const data = await client.getVisits(params, signal);
        pageCache.value.set(cacheKey, { data, fetchedAt: Date.now() });
      } catch {
        // Prefetch failures are non-critical — swallow silently
      } finally {
        prefetchPromises.delete(cacheKey);
      }
    })();

    prefetchPromises.set(cacheKey, promise);
    await promise;
  };

  /**
   * Fetch visit results with per-visit-ID caching.
   *
   * Purpose:
   * Retrieve visit results by ID with patient information.
   * Uses resultsCache to avoid redundant network calls when the same visit
   * is opened multiple times (e.g. flags dialog).
   *
   * When `silent` is true and cached data already exists, the loading flag is
   * NOT set so the UI shows no skeleton/overlay — the fresh data replaces the
   * stale data in the background. Errors in silent mode are still logged but
   * do NOT replace the existing cached results.
   *
   * @param id - Visit ID
   * @param signal - Optional abort signal
   * @param silent - When true, skip loading state if cache already has data
   *                 AND always revalidate in the background (SWR on re-mount)
   */
  const fetchVisitResults = async (
    id: number,
    signal?: AbortSignal | null,
    silent = false,
  ): Promise<VisitResults | null> => {
    // Check per-visit results cache first.
    // When silent=true the caller already has cached data on screen and wants
    // a background revalidation, so we skip the fresh-cache early-return and
    // always proceed to the network fetch below.
    const cachedResult = resultsCache.value.get(id);
    if (!silent && cachedResult && Date.now() - cachedResult.fetchedAt < STALE_TIME_MS) {
      currentVisitResults.value = cachedResult.data;
      return cachedResult.data;
    }

    // hasCachedData must be ID-aware: stale results from a *different* visit
    // must not suppress the error state when the new fetch fails.
    const hasCachedData = currentVisitResults.value?.id === id;
    const showLoading = !silent || !hasCachedData;

    if (showLoading) {
      loading.value = true;
      error.value = null;
      // Clear stale results from a different visit so the dialog renders
      // the correct error/loading state instead of mismatched data.
      if (!hasCachedData) {
        currentVisitResults.value = null;
      }
    }

    try {
      const token = await getToken();
      const client = new VisitsClient(config.public.API_BASE_URL as string, token);
      const results = await client.getVisitResults(id, signal);
      currentVisitResults.value = results;
      resultsCache.value.set(id, { data: results, fetchedAt: Date.now() });
      // Clear any previous error on success
      error.value = null;
      return results;
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return null;

      // Chaos force-empty: return typed empty results so the UI shows
      // the same zero-data state as a real empty visit.
      if (isChaosEmptySignal(err)) {
        const emptyResults: VisitResults = {
          id,
          date: new Date().toISOString(),
          patient: {
            id: 1,
            pidTypeId: 1,
            pidTypeIdName: "ЕГН",
            pidTypeIdIsIdentity: true,
            patientId: "7501011234",
            isMale: true,
            speciesId: 1,
            dateOfBirth: "1975-01-01",
            givenName: "Александър",
            middleName: "Тестов",
            familyName: "Пациент",
          },
          departmentResults: [],
          samples: [],
        };
        currentVisitResults.value = emptyResults;
        error.value = null;
        return emptyResults;
      }

      // 404 from the results endpoint means the patient has not paid.
      // Treat as a payment-required state rather than a generic API error.
      const statusCode = (err as { statusCode?: number }).statusCode;
      if (statusCode === 404) {
        isPaymentRequired.value = true;
        return null;
      }

      // Non-404 error — clear any stale payment-required flag so the error
      // state takes priority over a previously cached payment gate.
      isPaymentRequired.value = false;

      const type = classifyError(err);
      if (type !== "abort") {
        if (!hasCachedData) {
          // First load failure — inline error state
          error.value = {
            type,
            message: getErrorMessage(type, undefined, (err as Error)?.message),
          };
        } else {
          // Refresh failure — stale data stays visible, snackbar as only feedback.
          // Use the contextual "showing last known data" message so the user
          // understands the table content is stale, not an empty or broken state.
          showSnackbar(t("components.common.snackbar.actions.refreshFailed"), {
            color: "error",
            icon: "mdi-alert-circle-outline",
          });
        }
      }
      console.error("[useVisits] Error fetching visit results:", err);
      return null;
    } finally {
      if (showLoading) {
        loading.value = false;
      }
    }
  };

  /**
   * Fetch visit balance
   *
   * Purpose:
   * Retrieve visit financial balance by ID with patient information.
   *
   * When `silent` is true and cached data already exists, the loading flag is
   * NOT set so the UI shows no skeleton/overlay — the fresh data replaces the
   * stale data in the background. Errors in silent mode are still logged but
   * do NOT replace the existing cached balance.
   *
   * @param id - Visit ID
   * @param signal - Optional abort signal
   * @param silent - When true, skip loading state if cache already has data
   */
  const fetchVisitBalance = async (
    id: number,
    signal?: AbortSignal | null,
    silent = false,
  ): Promise<VisitBalance | null> => {
    const hasCachedData = currentVisitBalance.value !== null;
    const showLoading = !silent || !hasCachedData;

    if (showLoading) {
      loading.value = true;
      error.value = null;
    }

    try {
      const token = await getToken();
      const client = new VisitsClient(config.public.API_BASE_URL as string, token);
      const balance = await client.getVisitBalance(id, signal);
      currentVisitBalance.value = balance;
      error.value = null;
      return balance;
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return null;

      // Chaos force-empty: return typed empty balance so the UI shows
      // the same zero-data state as a real empty balance response.
      if (isChaosEmptySignal(err)) {
        const emptyBalance: VisitBalance = {
          id,
          date: new Date().toISOString(),
          patient: {
            id: 1,
            pidTypeId: 1,
            pidTypeIdName: "ЕГН",
            pidTypeIdIsIdentity: true,
            patientId: "7501011234",
            isMale: true,
            speciesId: 1,
            dateOfBirth: "1975-01-01",
            givenName: "Александър",
            middleName: "Тестов",
            familyName: "Пациент",
          },
          sales: [],
          balance: 0,
        };
        currentVisitBalance.value = emptyBalance;
        error.value = null;
        return emptyBalance;
      }

      // 404 from the balance endpoint means no balance data exists.
      // Treat as an empty balance rather than a generic API error.
      const statusCode = (err as { statusCode?: number }).statusCode;
      if (statusCode === 404) {
        const emptyBalance: VisitBalance = {
          id,
          date: "",
          patient: {
            id: 0,
            pidTypeId: 0,
            pidTypeIdName: "",
            pidTypeIdIsIdentity: false,
            patientId: "",
            isMale: false,
            speciesId: 0,
            dateOfBirth: "",
            givenName: "",
            middleName: "",
            familyName: "",
          },
          sales: [],
          balance: 0,
        };
        currentVisitBalance.value = emptyBalance;
        error.value = null;
        return emptyBalance;
      }

      const type = classifyError(err);
      if (type !== "abort") {
        if (!hasCachedData) {
          // First load failure — inline error state
          error.value = {
            type,
            message: getErrorMessage(type, undefined, (err as Error)?.message),
          };
        } else {
          // Refresh failure — stale data stays visible, snackbar as only feedback.
          // Use the contextual "showing last known data" message so the user
          // understands the balance content is stale, not an empty or broken state.
          showSnackbar(t("components.common.snackbar.actions.refreshFailed"), {
            color: "error",
            icon: "mdi-alert-circle-outline",
          });
        }
      }
      console.error("[useVisits] Error fetching visit balance:", err);
      return null;
    } finally {
      if (showLoading) {
        loading.value = false;
      }
    }
  };

  /**
   * Clear cached visit balance
   *
   * Purpose:
   * Reset currentVisitBalance to null so the next fetchVisitBalance call
   * renders the skeleton loader from scratch. Used by manual refresh.
   */
  const clearBalanceCache = () => {
    currentVisitBalance.value = null;
    if (import.meta.client) {
      sessionStorage.removeItem(`${key}.currentBalance`);
    }
  };

  /**
   * Clear cached visit results
   *
   * Purpose:
   * Reset currentVisitResults to null so the next fetchVisitResults call
   * renders the skeleton loader from scratch. Used by manual refresh to give
   * the user full visual feedback that a reload is in progress.
   * Also clears isPaymentRequired so the payment gate re-evaluates on the
   * fresh response (in case the patient has paid between refreshes).
   */
  const clearResultsCache = () => {
    currentVisitResults.value = null;
    isPaymentRequired.value = false;
    resultsCache.value.clear();
    if (import.meta.client) {
      sessionStorage.removeItem(`${key}.currentResults`);
    }
  };

  /**
   * Clear cached visits list and page cache
   *
   * Purpose:
   * Reset visits to null and clear the page cache map so the next fetchVisits
   * call renders the skeleton loader from scratch. Used by manual refresh.
   */
  const clearVisitsCache = () => {
    visits.value = null;
    pageCache.value.clear();
    resultsCache.value.clear();
  };

  /**
   * Invalidate the in-memory SWR page and results caches WITHOUT clearing
   * the visible visits data.  This forces the next fetch to hit the network
   * while keeping stale data visible so that on failure the composable takes
   * the snackbar path (hasCachedData=true) instead of the inline error path.
   *
   * Used by manual refresh — the user sees stale data with a loading overlay
   * and gets a snackbar if the refresh fails.
   */
  const invalidatePageCache = (): void => {
    pageCache.value.clear();
    resultsCache.value.clear();
  };

  /**
   * Clear the current error state.
   *
   * Used by page composables that manage their own error lifecycle —
   * e.g. clearing a stale error when the user changes filters so the
   * previous query’s error doesn’t bleed into the new query.
   */
  const clearError = (): void => {
    error.value = null;
  };
  /**
   * Restore visits data from an external snapshot and clear any current error.
   *
   * Used by the page orchestrator to restore unfiltered data when a filter is
   * cleared after a failed filtered fetch. Without this the table would show
   * empty/error state because clearVisitsCache destroyed the original data
   * before the filtered fetch ran.
   *
   * @param data - The snapshot to write back into visits state
   */
  const restoreVisits = (data: PaginatedResponse<Visit>): void => {
    visits.value = data;
    error.value = null;
  };
  /**
   * Pre-populate the results cache from external data.
   *
   * Used for cross-tab warm start: when the corporate results page is
   * opened via window.open(), it reads the parent tab's sessionStorage
   * (which is copied to the new tab) and seeds its own cache so the
   * user sees data instantly while a silent revalidation runs.
   *
   * Uses fetchedAt=0 so the entry is always treated as stale — the next
   * fetchVisitResults call with silent=true will revalidate in the
   * background without showing a loading overlay.
   *
   * @param id - Visit ID
   * @param data - The VisitResults to seed
   */
  const seedVisitResults = (id: number, data: VisitResults): void => {
    currentVisitResults.value = data;
    resultsCache.value.set(id, { data, fetchedAt: 0 });
  };

  /**
   * Seed the in-memory pageCache with the current visits data as a stale entry.
   *
   * Called by the page orchestrator on init when visits data was restored
   * from sessionStorage after a browser reload. This turns the initial
   * fetch into a cache HIT (serve stale → silent revalidation) instead
   * of a miss that would show a loading skeleton.
   *
   * Uses fetchedAt=0 so the entry is always treated as stale.
   *
   * @param params - The pagination params matching the current visits data
   */
  const seedCurrentPage = (params: PaginationParams): void => {
    if (!visits.value) return;
    const cacheKey = pageCacheKey(params);
    if (!pageCache.value.has(cacheKey)) {
      pageCache.value.set(cacheKey, { data: visits.value, fetchedAt: 0 });
    }
  };

  //
  // ============================================================================
  // RETURN PUBLIC API
  // ============================================================================
  //

  return {
    // State (readonly for external consumers)
    loading: readonly(loading),
    visits: readonly(visits),
    currentVisitResults: readonly(currentVisitResults),
    currentVisitBalance: readonly(currentVisitBalance),
    error: readonly(error),
    isPaymentRequired: readonly(isPaymentRequired),

    // Methods
    fetchVisits,
    fetchVisitResults,
    fetchVisitBalance,
    prefetchVisits,
    seedCurrentPage,
    seedVisitResults,
    clearResultsCache,
    clearBalanceCache,
    clearVisitsCache,
    invalidatePageCache,
    clearError,
    restoreVisits,
  };
};
